'use client';

import { useRef, useState } from 'react';
import { motion } from 'motion/react';
import { useTranslations, useLocale } from 'next-intl';
import { Link } from '@/i18n/navigation';
import {
  Package, Layers, Users, ArrowRight,
  ChevronLeft, ChevronRight, QrCode, Scissors, Download,
  SlidersHorizontal, CreditCard, Check,
  Monitor, Apple, Smartphone,
} from 'lucide-react';

// URL di download dell'app desktop Windows (GitHub Releases, sempre l'ultima versione).
const DOWNLOAD_WINDOWS_URL = 'https://github.com/Catta00/lithosbase-desktop/releases/latest/download/Lithosbase-Setup.exe';
import { SiteNav } from '@/components/SiteNav';
import { AppAccessDialog, type Piattaforma } from '@/components/AppAccessDialog';
import {
  Reveal, ShotStockTable, ShotCatalog, ShotTeam, ShotSettings,
  FeatureRow, MarketingFooter,
} from '@/components/marketing';
import { BETA_LAUNCH } from '@/lib/beta';

const EASE = [0.22, 1, 0.36, 1] as const;

const SUPABASE_PHOTOS = 'https://hatmwfdriyykbwcnepwr.supabase.co/storage/v1/object/public/slab-photos';

const STRIP_MATERIALS = [
  { name: 'Carrara Bianco',    dim: '320 × 160 cm', cat: 'Marmi',      photo: 'bianco-carrara-c.jpg' },
  { name: 'Nero Marquina',     dim: '280 × 140 cm', cat: 'Marmi',      photo: 'nero-assoluto-zimbabwe.jpg' },
  { name: 'Calacatta Oro',     dim: '310 × 155 cm', cat: 'Marmi',      photo: 'calacatta-oro.jpg' },
  { name: 'Statuario Venato',  dim: '300 × 160 cm', cat: 'Marmi',      photo: 'statuario-venato.jpg' },
  { name: 'Travertino Romano', dim: '320 × 155 cm', cat: 'Travertino', photo: 'travertino-romano-classico.jpg' },
  { name: 'Grigio Carnico',    dim: '270 × 140 cm', cat: 'Graniti',    photo: 'grigio-carnico.jpg' },
  { name: 'Quarella Ombria',   dim: '290 × 150 cm', cat: 'Quarzi',     photo: 'quarella-ombria.jpg' },
  { name: 'Bianco Perla',      dim: '310 × 155 cm', cat: 'Marmi',      photo: 'bianco-perla.jpg' },
] as const;

const STRIP_LONG = [...STRIP_MATERIALS, ...STRIP_MATERIALS, ...STRIP_MATERIALS];

function MaterialsStrip() {
  const t = useTranslations('Landing');
  const tCommon = useTranslations('Common');
  const locale = useLocale();
  const ref = useRef<HTMLDivElement>(null);
  const scroll = (dir: 1 | -1) => {
    ref.current?.scrollBy({ left: dir * 460, behavior: 'smooth' });
  };

  return (
    <div className="pb-[clamp(64px,9vw,128px)]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between gap-6 mb-6">
          <Reveal>
            <div
              className="flex items-center gap-2.5 font-mono text-[12px] font-medium tracking-[0.14em] uppercase mb-3.5"
              style={{ color: 'var(--accent)' }}
            >
              {t('featureCatalog.eyebrow')}
            </div>
            <h2
              className="font-bold tracking-[-0.025em] leading-[1.1] m-0 max-w-[18ch]"
              style={{ fontSize: 'clamp(28px,3.4vw,40px)' }}
            >
              {t('featureCatalog.sectionTitle')}
            </h2>
          </Reveal>
          <div className="flex gap-2 shrink-0 pb-1">
            {([-1, 1] as const).map((dir) => (
              <button
                key={dir}
                onClick={() => scroll(dir)}
                aria-label={dir === -1 ? tCommon('prev') : tCommon('next')}
                className="w-11 h-11 rounded-full bg-stone-50 dark:bg-zinc-900 border border-stone-300 dark:border-zinc-700 flex items-center justify-center text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800 hover:border-stone-950 dark:hover:border-zinc-50 transition-all active:scale-95"
              >
                {dir === -1 ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div
        ref={ref}
        className="flex gap-4 overflow-x-auto pb-6 mt-2"
        style={{
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          paddingLeft: 'max(24px, calc(50vw - 592px))',
          paddingRight: '48px',
        }}
      >
        {STRIP_LONG.map((m, i) => (
          /* /c/ routes are NOT locale-prefixed — use plain <a> */
          <a
            key={i}
            href={`/c/workshop?lang=${locale}`}
            className="flex-none flex flex-col rounded-[16px] overflow-hidden bg-stone-50 dark:bg-zinc-900 border border-stone-200 dark:border-zinc-800 hover:-translate-y-1 hover:shadow-md transition-all duration-250"
            style={{ width: 220, scrollSnapAlign: 'start' }}
          >
            <div className="relative" style={{ aspectRatio: '4/5' }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={`${SUPABASE_PHOTOS}/${m.photo}`}
                alt={m.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="px-3.5 py-3">
              <div className="text-[14px] font-semibold text-stone-950 dark:text-zinc-50 leading-[1.2] tracking-[-0.005em]">
                {m.name}
              </div>
              <div className="font-mono text-[11.5px] text-stone-400 dark:text-zinc-500 mt-1">
                {m.cat} · {m.dim}
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

export default function LandingContent() {
  // Quale app sta chiedendo, o null se il riquadro e' chiuso. Le tre schede
  // aprono lo stesso riquadro cambiando solo questa parola.
  const [chiedeApp, setChiedeApp] = useState<Piattaforma | null>(null);
  const t = useTranslations('Landing');
  const tCommon = useTranslations('Common');
  const locale = useLocale();

  const PILLARS = [
    { n: '01', Icon: Package,          href: '/#stock',    key: '01' as const },
    { n: '02', Icon: Layers,           href: '/#catalog',  key: '02' as const },
    { n: '03', Icon: Users,            href: '/#team',     key: '03' as const },
    { n: '04', Icon: SlidersHorizontal, href: '/#settings', key: '04' as const },
    { n: '05', Icon: CreditCard,       href: '/#pricing',  key: '05' as const },
  ];

  const PRICING_FEATURES = [
    { Icon: Package,  fKey: 'f_stock' as const },
    { Icon: Layers,   fKey: 'f_catalog' as const },
    { Icon: Users,    fKey: 'f_team' as const },
    { Icon: QrCode,   fKey: 'f_qr' as const },
    { Icon: Download, fKey: 'f_export' as const },
    { Icon: Scissors, fKey: 'f_cut' as const },
  ];

  return (
    <div className="flex flex-col bg-stone-50 dark:bg-zinc-950 text-stone-950 dark:text-zinc-50">
      <SiteNav />

      {/* ── Hero ── */}
      <section
        className="relative overflow-hidden"
        style={{ padding: 'clamp(72px,11vw,144px) 0 clamp(80px,12vw,160px)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none dark:hidden"
          style={{
            backgroundImage: 'url(/marble-light.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.45,
            maskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none hidden dark:block"
          style={{
            backgroundImage: 'url(/marble-dark.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.6,
            maskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
          }}
        />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-black tracking-[-0.04em] leading-[0.98] m-0 mb-6 max-w-[14ch]"
            style={{ fontSize: 'clamp(56px,9vw,96px)' }}
          >
            {t('hero.h1a')}{' '}
            <span
              style={{
                background:
                  'linear-gradient(transparent 78%, var(--accent-soft) 78%, var(--accent-soft) 96%, transparent 96%)',
                padding: '0 4px',
              }}
            >
              {t('hero.h1b')}
            </span>{' '}
            {t('hero.h1c')}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.22, ease: EASE }}
            className="text-[19px] leading-[1.55] text-stone-500 dark:text-zinc-400 max-w-[52ch] m-0 mb-9"
          >
            {t('hero.body')}
          </motion.p>

          {/* Beta program — azione primaria: candidatura a un posto (gating BETA_LAUNCH) */}
          {BETA_LAUNCH && (
            <div
              className="mb-7 rounded-[16px] border px-6 py-6 sm:px-7 sm:py-7 max-w-[640px]"
              style={{ borderColor: 'var(--accent)', background: 'var(--accent-soft)' }}
            >
              <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.12em] uppercase mb-2.5" style={{ color: 'var(--accent-strong)' }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--accent)' }} />
                {t('beta.badge')}
              </div>
              <h2 className="text-[20px] sm:text-[23px] font-bold tracking-[-0.02em] text-stone-950 dark:text-zinc-50 mb-1.5">{t('beta.title')}</h2>
              <p className="text-[14px] sm:text-[15px] text-stone-600 dark:text-zinc-300 leading-relaxed mb-1">{t('beta.body')}</p>
              <p className="text-[13.5px] sm:text-[14px] font-semibold text-stone-800 dark:text-zinc-200 leading-relaxed mb-4" style={{ color: 'var(--accent-strong)' }}>{t('beta.spots')}</p>

              <ul className="flex flex-col gap-2 mb-5">
                {(['perk1', 'perk2', 'perk3'] as const).map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-[14px] text-stone-700 dark:text-zinc-300 leading-snug">
                    <Check size={17} className="shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                    {t(`beta.${p}`)}
                  </li>
                ))}
              </ul>

              <Link href="/start" className="inline-flex items-center justify-center gap-2 px-6 py-[13px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity">
                {t('beta.cta')} <ArrowRight size={15} />
              </Link>
              <p className="text-[12.5px] text-stone-500 dark:text-zinc-400 leading-relaxed mt-3">{t('beta.note')}</p>
              <p className="text-[12px] font-medium text-stone-500 dark:text-zinc-400 tracking-[0.01em] mt-1.5">{t('beta.trust')}</p>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.32, ease: EASE }}
            className="flex flex-wrap items-center gap-x-5 gap-y-3"
          >
            {/* Quando BETA_LAUNCH è false: torna il set completo di 3 bottoni (azione primaria piena) */}
            {!BETA_LAUNCH && (
              <Link
                href="/start"
                className="inline-flex items-center gap-2 px-6 py-[14px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity"
              >
                {t('hero.ctaCreate')}
              </Link>
            )}

            {BETA_LAUNCH ? (
              <>
                {/* Azioni secondarie leggere sotto il box beta */}
                <Link
                  href="/demo"
                  className="inline-flex items-center gap-1.5 text-[14px] font-medium text-stone-600 dark:text-zinc-400 hover:text-stone-950 dark:hover:text-zinc-50 transition-colors underline underline-offset-4 decoration-stone-300 dark:decoration-zinc-700 hover:decoration-stone-950 dark:hover:decoration-zinc-50"
                >
                  {t('hero.ctaDemo')}
                </Link>
                {/* /c/ routes are NOT locale-prefixed — use plain <a> */}
                <a
                  href={`/c/workshop?lang=${locale}`}
                  className="inline-flex items-center gap-1.5 text-[14px] font-medium text-stone-600 dark:text-zinc-400 hover:text-stone-950 dark:hover:text-zinc-50 transition-colors underline underline-offset-4 decoration-stone-300 dark:decoration-zinc-700 hover:decoration-stone-950 dark:hover:decoration-zinc-50"
                >
                  {t('hero.ctaCatalog')}
                </a>
              </>
            ) : (
              <>
                <Link
                  href="/demo"
                  className="inline-flex items-center gap-2 px-6 py-[14px] text-[15px] font-medium rounded-[10px] border border-stone-300 dark:border-zinc-700 text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-900 transition-colors"
                >
                  {t('hero.ctaDemo')}
                </Link>
                {/* /c/ routes are NOT locale-prefixed — use plain <a> */}
                <a
                  href={`/c/workshop?lang=${locale}`}
                  className="inline-flex items-center gap-2 px-6 py-[14px] text-[15px] font-medium rounded-[10px] border border-stone-300 dark:border-zinc-700 text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-900 transition-colors"
                >
                  {t('hero.ctaCatalog')}
                </a>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── Problema (PAS) ── sempre visibile, tra hero e pillars */}
      <section className="pt-2 pb-[clamp(40px,6vw,80px)]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="max-w-[760px]">
              <div
                className="flex items-center gap-2.5 font-mono text-[12px] font-medium tracking-[0.14em] uppercase mb-4"
                style={{ color: 'var(--accent)' }}
              >
                {t('problem.eyebrow')}
              </div>
              <h2
                className="font-bold tracking-[-0.025em] leading-[1.12] m-0 mb-4"
                style={{ fontSize: 'clamp(26px,3.4vw,40px)' }}
              >
                {t('problem.title')}
              </h2>
              <p className="text-[17px] leading-[1.6] text-stone-500 dark:text-zinc-400 m-0">
                {t('problem.body')}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Pillars ── */}
      <section className="py-6">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-stone-200 dark:bg-zinc-800 border border-stone-200 dark:border-zinc-800 rounded-[20px] overflow-hidden">
              {PILLARS.map(({ n, Icon, href, key }) => (
                <Link
                  key={n}
                  href={href}
                  className="group flex flex-col min-h-[240px] p-6 gap-3 bg-stone-50 dark:bg-zinc-950 hover:bg-white dark:hover:bg-zinc-900 transition-colors sm:last:col-span-2 lg:last:col-span-1"
                >
                  <span
                    className="font-mono text-[12px] font-medium tracking-[0.14em] uppercase"
                    style={{ color: 'var(--accent)' }}
                  >
                    {n}
                  </span>
                  <div className="w-11 h-11 rounded-[10px] border border-stone-200 dark:border-zinc-800 flex items-center justify-center text-stone-700 dark:text-zinc-300 group-hover:bg-stone-100 dark:group-hover:bg-zinc-800 transition-colors">
                    <Icon size={22} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[18px] tracking-[-0.015em] leading-[1.2] m-0 mb-2">
                      {t(`pillars.${key}.title`)}
                    </h3>
                    <p className="text-[13px] leading-[1.55] text-stone-500 dark:text-zinc-400 m-0">
                      {t(`pillars.${key}.desc`)}
                    </p>
                  </div>
                  <span className="mt-auto inline-flex items-center gap-1.5 text-[13px] font-medium text-stone-400 dark:text-zinc-500 group-hover:text-stone-800 dark:group-hover:text-zinc-200 transition-colors">
                    {tCommon('learnMore')} <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Feature: Stock ── */}
      <FeatureRow
        id="stock"
        num="01"
        label={t('featureStock.label')}
        title={t('featureStock.title')}
        body={t('featureStock.body')}
        bullets={[
          t('featureStock.b1'),
          t('featureStock.b2'),
          t('featureStock.b3'),
          t('featureStock.b4'),
        ]}
        shot={<ShotStockTable />}
      />

      {/* ── Feature: Catalogo (flip) ── */}
      <FeatureRow
        id="catalog"
        num="02"
        label={t('featureCatalog.label')}
        title={t('featureCatalog.title')}
        body={t('featureCatalog.body')}
        bullets={[
          t('featureCatalog.b1'),
          t('featureCatalog.b2'),
          t('featureCatalog.b3'),
          t('featureCatalog.b4'),
        ]}
        shot={<ShotCatalog />}
        flip
        footer={<MaterialsStrip />}
      />

      {/* ── Feature: Team ── */}
      <FeatureRow
        id="team"
        num="03"
        label={t('featureTeam.label')}
        title={t('featureTeam.title')}
        body={t('featureTeam.body')}
        bullets={[
          t('featureTeam.b1'),
          t('featureTeam.b2'),
          t('featureTeam.b3'),
        ]}
        shot={<ShotTeam />}
      />

      {/* ── Feature: Impostazioni ── */}
      <FeatureRow
        id="settings"
        num="04"
        label={t('featureSettings.label')}
        title={t('featureSettings.title')}
        body={t('featureSettings.body')}
        bullets={[
          t('featureSettings.b1'),
          t('featureSettings.b2'),
          t('featureSettings.b3'),
          t('featureSettings.b4'),
        ]}
        shot={<ShotSettings />}
        flip
      />

      {/* ── Pricing teaser ── */}
      <section id="pricing" className="py-[clamp(64px,9vw,128px)]" style={{ scrollMarginTop: '64px' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal className="mb-14 max-w-[680px]">
            <div
              className="flex items-center gap-2.5 font-mono text-[12px] font-medium tracking-[0.14em] uppercase mb-4"
              style={{ color: 'var(--accent)' }}
            >
              {t('pricingTeaser.eyebrow')}
            </div>
            <h2
              className="font-bold tracking-[-0.025em] leading-[1.1] m-0 mb-4"
              style={{ fontSize: 'clamp(32px,4vw,48px)' }}
            >
              {t('pricingTeaser.title')}
            </h2>
            <p className="text-[17px] leading-[1.55] text-stone-500 dark:text-zinc-400 m-0">
              {t('pricingTeaser.body')}
            </p>
          </Reveal>

          <div className="grid gap-8 lg:gap-12 items-start lg:grid-cols-[1.4fr_1fr]">
            <Reveal>
              <div className="grid grid-cols-2 gap-3">
                {PRICING_FEATURES.map(({ Icon, fKey }) => (
                  <div
                    key={fKey}
                    className="rounded-[16px] p-6 bg-stone-50 dark:bg-zinc-950 border border-stone-200 dark:border-zinc-800 flex flex-col gap-2"
                  >
                    <div className="w-9 h-9 rounded-[8px] bg-stone-100 dark:bg-zinc-800 flex items-center justify-center mb-2">
                      <Icon size={18} className="text-stone-700 dark:text-zinc-300" />
                    </div>
                    <div className="font-semibold text-[17px] leading-[1.2] text-stone-950 dark:text-zinc-50">
                      {t(`pricingTeaser.${fKey}.title`)}
                    </div>
                    <div className="text-[14px] leading-[1.55] text-stone-500 dark:text-zinc-400">
                      {t(`pricingTeaser.${fKey}.desc`)}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1} className="flex flex-col gap-4 lg:sticky lg:top-20">
              {BETA_LAUNCH && (
                <div
                  className="rounded-[16px] border px-6 py-5 flex flex-col gap-2.5"
                  style={{ borderColor: 'var(--accent)', background: 'var(--accent-soft)' }}
                >
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-[6px] text-[11px] font-bold tracking-[0.08em] uppercase w-fit"
                    style={{ background: 'var(--accent)', color: 'var(--accent-foreground, #fff)' }}
                  >
                    {t('pricingTeaser.betaBadge')}
                  </span>
                  <p className="text-[14px] leading-[1.6] text-stone-600 dark:text-zinc-300 m-0">
                    {t('pricingTeaser.betaNote')}
                  </p>
                </div>
              )}
              <div className="rounded-[20px] p-7 bg-stone-50 dark:bg-zinc-950 flex flex-col gap-4">
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-[6px] text-[11px] font-medium tracking-[0.06em] uppercase w-fit"
                  style={{ background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}
                >
                  {t('pricingTeaser.cardBadge')}
                </span>
                <h3 className="text-[28px] font-bold tracking-[-0.02em] leading-[1.15] m-0">
                  {t('pricingTeaser.cardTitle')}
                </h3>
                <p className="text-[15px] leading-[1.55] text-stone-500 dark:text-zinc-400 m-0">
                  {t('pricingTeaser.cardBody')}
                </p>
                <div>
                  <div className="text-[32px] font-black tracking-[-0.02em] leading-none">
                    {t('pricingTeaser.priceLine')}
                  </div>
                  <div className="text-[13px] text-stone-500 dark:text-zinc-400 mt-1.5">
                    {t('pricingTeaser.priceNote')}
                  </div>
                </div>
                <Link
                  href="/start"
                  className="inline-flex items-center justify-center gap-2 px-6 py-[14px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity"
                >
                  {t('pricingTeaser.cardCta')}
                </Link>
                <Link
                  href="/pricing"
                  className="text-[13px] font-medium text-stone-400 dark:text-zinc-500 hover:text-stone-950 dark:hover:text-zinc-50 transition-colors text-center"
                >
                  {t('pricingTeaser.cardLink')}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Scarica l'app ── */}
      <section id="download" className="bg-stone-50 dark:bg-zinc-950" style={{ padding: 'clamp(56px,7vw,96px) 0' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div
              className="text-[11px] font-semibold tracking-[0.14em] uppercase mb-3"
              style={{ color: 'var(--accent)' }}
            >
              {t('download.eyebrow')}
            </div>
            <h2 className="font-bold tracking-[-0.025em] leading-[1.1] m-0 mb-3" style={{ fontSize: 'clamp(28px,3.4vw,40px)' }}>
              {t('download.title')}
            </h2>
            <p className="text-[17px] leading-[1.55] text-stone-500 dark:text-zinc-400 m-0 mb-10 max-w-[60ch]">
              {t('download.body')}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Windows */}
            <Reveal className="flex flex-col gap-3 p-6 rounded-[16px] border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <div className="w-11 h-11 rounded-[10px] flex items-center justify-center" style={{ background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}>
                <Monitor size={20} />
              </div>
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-[6px] text-[11px] font-medium tracking-[0.06em] uppercase w-fit" style={{ background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}>
                {t('download.badgeAvailable')}
              </span>
              <h3 className="text-[18px] font-bold m-0">{t('download.win.title')}</h3>
              <p className="text-[14px] leading-[1.5] text-stone-500 dark:text-zinc-400 m-0 flex-1">{t('download.win.desc')}</p>
              {DOWNLOAD_WINDOWS_URL ? (
                <a href={DOWNLOAD_WINDOWS_URL} className="inline-flex items-center justify-center gap-2 px-4 py-[12px] text-[14px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity">
                  <Download size={16} /> {t('download.win.cta')}
                </a>
              ) : (
                <span className="inline-flex items-center justify-center gap-2 px-4 py-[12px] text-[14px] font-semibold rounded-[10px] bg-stone-200 text-stone-500 dark:bg-zinc-800 dark:text-zinc-400 cursor-default">
                  {t('download.win.soon')}
                </span>
              )}
              <span className="text-[12px] text-stone-400 dark:text-zinc-500">{t('download.win.meta')}</span>
              <span className="text-[11px] leading-[1.45] text-stone-400 dark:text-zinc-500">{t('download.win.smartscreen')}</span>
            </Reveal>

            {/* macOS */}
            <Reveal className="flex flex-col gap-3 p-6 rounded-[16px] border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 opacity-80">
              <div className="w-11 h-11 rounded-[10px] flex items-center justify-center bg-stone-100 dark:bg-zinc-800 text-stone-500 dark:text-zinc-400">
                <Apple size={20} />
              </div>
              <span className="inline-flex items-center px-2 py-1 rounded-[6px] text-[11px] font-medium tracking-[0.06em] uppercase w-fit bg-stone-100 dark:bg-zinc-800 text-stone-500 dark:text-zinc-400">
                {t('download.badgeSoon')}
              </span>
              <h3 className="text-[18px] font-bold m-0">{t('download.mac.title')}</h3>
              <p className="text-[14px] leading-[1.5] text-stone-500 dark:text-zinc-400 m-0 flex-1">{t('download.mac.desc')}</p>
              <button type="button" onClick={() => setChiedeApp('mac')} className="inline-flex items-center justify-center gap-2 px-4 py-[12px] text-[14px] font-semibold rounded-[10px] border border-stone-300 dark:border-zinc-700 hover:border-stone-950 dark:hover:border-zinc-50 transition-colors">
                {t('download.mac.cta')}
              </button>
            </Reveal>

            {/* iOS */}
            <Reveal className="flex flex-col gap-3 p-6 rounded-[16px] border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <div className="w-11 h-11 rounded-[10px] flex items-center justify-center bg-stone-100 dark:bg-zinc-800 text-stone-700 dark:text-zinc-300">
                <Apple size={20} />
              </div>
              <span className="inline-flex items-center px-2 py-1 rounded-[6px] text-[11px] font-medium tracking-[0.06em] uppercase w-fit border" style={{ borderColor: 'var(--accent)', color: 'var(--accent-strong)' }}>
                {t('download.badgeBeta')}
              </span>
              <h3 className="text-[18px] font-bold m-0">{t('download.ios.title')}</h3>
              <p className="text-[14px] leading-[1.5] text-stone-500 dark:text-zinc-400 m-0 flex-1">{t('download.mobile.desc')}</p>
              <button type="button" onClick={() => setChiedeApp('ios')} className="inline-flex items-center justify-center gap-2 px-4 py-[12px] text-[14px] font-semibold rounded-[10px] border border-stone-300 dark:border-zinc-700 hover:border-stone-950 dark:hover:border-zinc-50 transition-colors">
                {t('download.betaCta')}
              </button>
            </Reveal>

            {/* Android */}
            <Reveal className="flex flex-col gap-3 p-6 rounded-[16px] border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <div className="w-11 h-11 rounded-[10px] flex items-center justify-center bg-stone-100 dark:bg-zinc-800 text-stone-700 dark:text-zinc-300">
                <Smartphone size={20} />
              </div>
              <span className="inline-flex items-center px-2 py-1 rounded-[6px] text-[11px] font-medium tracking-[0.06em] uppercase w-fit border" style={{ borderColor: 'var(--accent)', color: 'var(--accent-strong)' }}>
                {t('download.badgeBeta')}
              </span>
              <h3 className="text-[18px] font-bold m-0">{t('download.android.title')}</h3>
              <p className="text-[14px] leading-[1.5] text-stone-500 dark:text-zinc-400 m-0 flex-1">{t('download.mobile.desc')}</p>
              <button type="button" onClick={() => setChiedeApp('android')} className="inline-flex items-center justify-center gap-2 px-4 py-[12px] text-[14px] font-semibold rounded-[10px] border border-stone-300 dark:border-zinc-700 hover:border-stone-950 dark:hover:border-zinc-50 transition-colors">
                {t('download.betaCta')}
              </button>
            </Reveal>
          </div>

          <p className="text-[13px] text-stone-400 dark:text-zinc-500 mt-6">{t('download.reassure')}</p>
        </div>
      </section>

      {/* ── Dark CTA ── */}
      <section
        className="relative overflow-hidden bg-stone-950 text-stone-50"
        style={{ padding: 'clamp(64px,8vw,112px) 0' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'url(/marble-dark.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.35,
            maskImage: 'radial-gradient(ellipse at 30% 50%, black 0%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 30% 50%, black 0%, transparent 70%)',
          }}
        />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-10 flex-wrap">
            <div>
              <h2
                className="font-bold tracking-[-0.025em] leading-[1.05] m-0 mb-2 max-w-[14ch]"
                style={{ fontSize: 'clamp(36px,5vw,56px)' }}
              >
                {t('cta.title')}
              </h2>
              <p className="text-[17px] leading-[1.55] text-stone-400 m-0 max-w-[42ch]">
                {t('cta.body')}
              </p>
            </div>
            <Link
              href="/start"
              className="shrink-0 inline-flex items-center gap-2 px-7 py-[14px] text-[15px] font-semibold rounded-[10px] bg-stone-50 text-stone-950 hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              {t('cta.btn')} <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />

      <AppAccessDialog platform={chiedeApp} onClose={() => setChiedeApp(null)} />
    </div>
  );
}
